import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Linking,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MEANDER_APP_STORE_URL,
  MEANDER_PLAY_STORE_URL,
} from '../../constants/app-store-links';
import {
  detectWebMobilePlatform,
  WebMobilePlatform,
} from '../../utils/detect-web-mobile-platform';

interface HostMobileAppPromptModalProps {
  visible: boolean;
  onClose: () => void;
}

const HostMobileAppPromptModal: React.FC<HostMobileAppPromptModalProps> = ({
  visible,
  onClose,
}) => {
  const [platform, setPlatform] = useState<WebMobilePlatform>('unknown');

  useEffect(() => {
    if (visible) {
      setPlatform(detectWebMobilePlatform());
    }
  }, [visible]);

  const openStore = (url: string) => {
    Linking.openURL(url).catch(() => {});
  };

  const primaryStore =
    platform === 'ios'
      ? {
          url: MEANDER_APP_STORE_URL,
          icon: 'apple' as const,
          small: 'Download on the',
          large: 'App Store',
          label: 'Download MeanderEV on the App Store',
        }
      : platform === 'android'
      ? {
          url: MEANDER_PLAY_STORE_URL,
          icon: 'google-play' as const,
          small: 'Get it on',
          large: 'Google Play',
          label: 'Get MeanderEV on Google Play',
        }
      : null;

  const secondaryStore =
    platform === 'ios'
      ? {
          url: MEANDER_PLAY_STORE_URL,
          label: 'Using Android? Get it on Google Play',
        }
      : platform === 'android'
      ? {
          url: MEANDER_APP_STORE_URL,
          label: 'Using iPhone? Download on the App Store',
        }
      : null;

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.backdrop}>
          <TouchableWithoutFeedback onPress={() => {}}>
            <View style={styles.card}>
              <Pressable
                onPress={onClose}
                style={styles.closeButton}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <MaterialCommunityIcons name="close" size={22} color="#8D8E90" />
              </Pressable>

              <View style={styles.iconWrap}>
                <MaterialCommunityIcons
                  name="cellphone-arrow-down"
                  size={48}
                  color="#64E280"
                />
              </View>

              <Text style={styles.title}>List your charger in the app</Text>
              <Text style={styles.message}>
                The best way to become a MeanderEV host is on our mobile app.
                Download it to list your charger, verify your account, and start
                earning — the experience is built for your phone.
              </Text>

              {primaryStore ? (
                <>
                  <Pressable
                    onPress={() => openStore(primaryStore.url)}
                    style={({ pressed }) => [
                      styles.primaryStoreButton,
                      pressed && styles.buttonPressed,
                    ]}
                    accessibilityRole="link"
                    accessibilityLabel={primaryStore.label}
                  >
                    <MaterialCommunityIcons
                      name={primaryStore.icon}
                      size={24}
                      color="#000000"
                    />
                    <View style={styles.storeButtonTextWrap}>
                      <Text style={styles.storeButtonSmall}>
                        {primaryStore.small}
                      </Text>
                      <Text style={styles.storeButtonLarge}>
                        {primaryStore.large}
                      </Text>
                    </View>
                  </Pressable>

                  {secondaryStore && (
                    <Pressable
                      onPress={() => openStore(secondaryStore.url)}
                      style={({ pressed }) => [
                        styles.secondaryLink,
                        pressed && styles.buttonPressed,
                      ]}
                      accessibilityRole="link"
                    >
                      <Text style={styles.secondaryLinkText}>
                        {secondaryStore.label}
                      </Text>
                    </Pressable>
                  )}
                </>
              ) : (
                <View style={styles.storeRow}>
                  <Pressable
                    onPress={() => openStore(MEANDER_APP_STORE_URL)}
                    style={({ pressed }) => [
                      styles.storeButton,
                      pressed && styles.buttonPressed,
                    ]}
                    accessibilityRole="link"
                    accessibilityLabel="Download on the App Store"
                  >
                    <MaterialCommunityIcons name="apple" size={22} color="#FFFFFF" />
                    <View style={styles.storeButtonTextWrap}>
                      <Text style={styles.storeButtonSmallLight}>Download on the</Text>
                      <Text style={styles.storeButtonLargeLight}>App Store</Text>
                    </View>
                  </Pressable>

                  <Pressable
                    onPress={() => openStore(MEANDER_PLAY_STORE_URL)}
                    style={({ pressed }) => [
                      styles.storeButton,
                      pressed && styles.buttonPressed,
                    ]}
                    accessibilityRole="link"
                    accessibilityLabel="Get it on Google Play"
                  >
                    <MaterialCommunityIcons
                      name="google-play"
                      size={22}
                      color="#FFFFFF"
                    />
                    <View style={styles.storeButtonTextWrap}>
                      <Text style={styles.storeButtonSmallLight}>Get it on</Text>
                      <Text style={styles.storeButtonLargeLight}>Google Play</Text>
                    </View>
                  </Pressable>
                </View>
              )}

              <Text style={styles.helperText}>
                Already have the app? Open MeanderEV and tap{' '}
                <Text style={styles.helperTextEmphasis}>Host My Charger</Text>{' '}
                from the home screen or side menu.
              </Text>

              <Pressable
                onPress={onClose}
                style={({ pressed }) => [
                  styles.dismissButton,
                  pressed && styles.buttonPressed,
                ]}
                accessibilityRole="button"
              >
                <Text style={styles.dismissButtonText}>Got it</Text>
              </Pressable>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#1A1D21',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#2A2D32',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 24,
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: 4,
    zIndex: 1,
  },
  iconWrap: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(100, 226, 128, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  title: {
    fontFamily: 'Poppins-Bold',
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 10,
  },
  message: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 14,
    lineHeight: 20,
    color: '#C7CACF',
    textAlign: 'center',
    marginBottom: 20,
  },
  primaryStoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#64E280',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 999,
    minWidth: 240,
    justifyContent: 'center',
  },
  storeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    width: '100%',
  },
  storeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#020706',
    borderWidth: 1,
    borderColor: '#39BEB7',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    minWidth: 160,
  },
  storeButtonTextWrap: {
    flexDirection: 'column',
  },
  storeButtonSmall: {
    color: '#1A1D21',
    fontSize: 10,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  storeButtonLarge: {
    color: '#000000',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Bold',
  },
  storeButtonSmallLight: {
    color: '#C7CACF',
    fontSize: 10,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  storeButtonLargeLight: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Bold',
  },
  secondaryLink: {
    marginTop: 14,
    paddingVertical: 4,
  },
  secondaryLinkText: {
    color: '#39BEB7',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Medium',
    fontWeight: '500',
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
  helperText: {
    marginTop: 18,
    color: '#8D8E90',
    fontSize: 12,
    lineHeight: 17,
    textAlign: 'center',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  helperTextEmphasis: {
    color: '#64E280',
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Medium',
  },
  dismissButton: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  dismissButtonText: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Medium',
    fontWeight: '500',
  },
  buttonPressed: {
    opacity: 0.75,
  },
});

export default HostMobileAppPromptModal;
